import { Code2, TestTube2, Users, Cloud, Brain, type LucideIcon } from "lucide-react";

export interface ServiceDefinition {
  slug: string;
  icon: LucideIcon;
  title: string;
  shortDescription: string;
  description: string;
  overview: string[];
  bullets: string[];
  outcomes: string[];
  relatedSlugs: string[];
}

export const services: ServiceDefinition[] = [
  {
    slug: "qa",
    icon: TestTube2,
    title: "Quality Engineering",
    shortDescription:
      "Quality practices that help deliver reliable, high-performing, defect-resistant software.",
    description:
      "Quality Engineering strengthens every release with consulting, functional QA, automation, performance engineering, security testing, Salesforce QA, and test data management.",
    overview: [
      "Quality Engineering is built for teams that need confidence in every release. We evaluate the product from the user's point of view, validate critical workflows, and create a testing strategy that fits the speed and complexity of your delivery cycle.",
      "The practice covers early quality consulting, functional validation, automation, performance, security, privacy, Salesforce testing, and test data management. That gives your team stronger release readiness without slowing product momentum.",
    ],
    bullets: [
      "Quality consulting",
      "Functional quality assurance",
      "Test automation",
      "Test Center of Excellence",
      "DevTestOps",
      "Performance engineering",
      "Security and privacy quality assurance",
      "Salesforce quality assurance",
      "Test data management",
    ],
    outcomes: [
      "Reduced production defects and support escalations",
      "Faster regression cycles through practical automation",
      "Clear release readiness signals for product and engineering leaders",
      "Better user experience across functionality, performance, and security",
    ],
    relatedSlugs: ["ai-ml", "custom-software"],
  },
  {
    slug: "ai-ml",
    icon: Brain,
    title: "AI Engineering",
    shortDescription:
      "Production-ready AI and machine learning solutions tailored to real business problems.",
    description:
      "AI Engineering helps businesses build and scale machine learning through custom model development, analytics, MLOps, secure deployment, and practical AI strategy.",
    overview: [
      "AI Engineering focuses on moving artificial intelligence from experiments into dependable business systems. We help identify the right use cases, select the right modeling approach, and build solutions that are measurable, maintainable, and ready for production.",
      "The work can include custom machine learning models, data analytics solutions, model deployment, monitoring, MLOps pipelines, and AI strategy. Each engagement is shaped around business impact, responsible implementation, and long-term operating value.",
    ],
    bullets: [
      "Custom model development",
      "Data analytics solutions",
      "Model deployment and MLOps",
      "AI strategy and consulting",
      "Predictive analytics",
      "Secure production deployment",
    ],
    outcomes: [
      "AI use cases selected around measurable business value",
      "Models and analytics integrated into real workflows",
      "Scalable deployment pipelines for batch and real-time inference",
      "Monitoring practices that keep AI systems accurate and reliable",
    ],
    relatedSlugs: ["cloud", "custom-software"],
  },
  {
    slug: "salesforce",
    icon: Users,
    title: "Salesforce and ERP Implementation",
    shortDescription:
      "Business and technical Salesforce expertise for implementation, integration, optimization, and support.",
    description:
      "Salesforce and ERP Implementation helps teams realize the full potential of Salesforce through custom development, consulting, implementation, integrations, support, digital marketing services, org health checks, and jumpstart packages.",
    overview: [
      "Salesforce and ERP Implementation brings business process understanding together with platform engineering. We help teams configure, customize, integrate, and support Salesforce so it reflects the way the business actually operates.",
      "From full-scale implementation to health checks, integration, digital marketing services, and focused jumpstart packages, the goal is to make Salesforce easier to adopt, easier to maintain, and more valuable for sales, service, and operations teams.",
    ],
    bullets: [
      "Custom Salesforce development",
      "Salesforce consulting",
      "Full-scale Salesforce implementation",
      "Integration services",
      "Support and re-implementation",
      "Digital marketing services",
      "Salesforce org health check",
      "Financial Service Jumpstart Package",
      "Service Jumpstart Package",
      "Sales Jumpstart Package",
    ],
    outcomes: [
      "A Salesforce setup aligned with real business workflows",
      "Cleaner integrations across ERP, marketing, and operational systems",
      "Improved adoption through practical configuration and support",
      "Faster launches with focused financial, service, and sales jumpstart packages",
    ],
    relatedSlugs: ["custom-software", "qa"],
  },
  {
    slug: "custom-software",
    icon: Code2,
    title: "Custom Development",
    shortDescription:
      "Bespoke web, mobile, and software solutions tailored to unique business needs.",
    description:
      "Custom Development turns business ideas into secure, scalable software through modernization, new product engineering, mobile apps, web applications, automation, and solution consulting.",
    overview: [
      "Custom Development is for teams that need software shaped around their business instead of forcing the business around a generic tool. We design and build secure web, mobile, and full-stack applications with a clear path from idea to launch.",
      "The practice also covers product modernization, automation, and solution consulting. Whether you are replacing legacy systems, building a new platform, or creating internal tools, the work is planned around maintainability, usability, and measurable business outcomes.",
    ],
    bullets: [
      "Product modernization",
      "New software development",
      "Mobile application development",
      "Web application development",
      "Robotic process automation",
      "Solution consulting",
    ],
    outcomes: [
      "Software designed around your exact workflows and users",
      "Modernized systems that are easier to maintain and extend",
      "Web and mobile applications that support business growth",
      "Automation that reduces repetitive manual work",
    ],
    relatedSlugs: ["qa", "cloud"],
  },
  {
    slug: "cloud",
    icon: Cloud,
    title: "Cloud Engineering",
    shortDescription:
      "Scalable, secure cloud infrastructure design, migration, management, and DevOps services.",
    description:
      "Cloud Engineering supports digital transformation with cloud consulting, migration, managed cloud operations, DevOps automation, and secure infrastructure design.",
    overview: [
      "Cloud Engineering helps organizations move, run, and improve systems in the cloud with confidence. We assess the current environment, design a practical roadmap, and build infrastructure that supports security, scalability, and operational resilience.",
      "The practice includes cloud consulting, migration, management, DevOps, automation, and best-practice guidance. It is designed for teams that want cloud platforms to be easier to operate, easier to scale, and easier to govern.",
    ],
    bullets: [
      "Cloud consulting",
      "Cloud migration",
      "Cloud management",
      "DevOps",
      "Scalable infrastructure design",
      "Security and best-practice guidance",
    ],
    outcomes: [
      "Cloud environments planned around business priorities and risk",
      "Lower migration disruption through structured transition planning",
      "More reliable delivery with DevOps and automation practices",
      "Secure, scalable platforms that are easier to monitor and manage",
    ],
    relatedSlugs: ["custom-software", "ai-ml"],
  },
];

export function getServiceBySlug(slug: string | undefined) {
  return services.find((s) => s.slug === slug);
}
